import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { guidesList } from '@/mocks/guides';
import { charactersList } from '@/mocks/characters';
import { useSeo } from '@/hooks/useSeo';
import { formatDate } from '@/utils/date';
import Breadcrumb from '@/components/base/Breadcrumb';
import FavoriteButton from '@/components/base/FavoriteButton';
import ShareModal from '@/components/feature/ShareModal';
import Reveal from '@/components/base/Reveal';

export default function GuideDetail() {
  const { id } = useParams<{ id: string }>();
  const [shareOpen, setShareOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(0);

  const guide = useMemo(() => guidesList.find((item) => item.id === id), [id]);
  const character = useMemo(
    () => (guide ? charactersList.find((item) => item.id === guide.characterId) : undefined),
    [guide],
  );

  useSeo({
    title: guide ? `${guide.title} – 原神官方游戏站` : '攻略详情 – 原神官方游戏站',
    description: guide ? guide.summary : '查看原神攻略详情。',
    keywords: guide ? guide.tags.join(',') : '原神攻略',
    canonicalPath: `/guides/${id}`,
  });

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.max(0, (window.scrollY / total) * 100)) : 0);

      if (guide) {
        let current = 0;
        guide.sections.forEach((section, index) => {
          const el = document.getElementById(section.heading);
          if (el && el.getBoundingClientRect().top < 160) current = index;
        });
        setActiveSection(current);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [guide]);

  if (!guide) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到这篇攻略</h1>
        <Link
          to="/characters"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-arrow-left-line"></i>
          返回角色攻略
        </Link>
      </div>
    );
  }

  const related = guidesList.filter((item) => guide.related.includes(item.id));
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div>
      <div className="fixed left-0 right-0 top-16 z-40 h-0.5 bg-background-200 md:top-[72px]">
        <div className="h-full bg-accent-500 transition-all duration-150" style={{ width: `${progress}%` }} />
      </div>

      <div className="relative h-[240px] w-full overflow-hidden md:h-[320px]">
        <img src={guide.cover} alt={guide.title} title={`${guide.title} 原神攻略`} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/30"></div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-6xl px-4 pb-8 md:px-6">
            <div className="text-background-50/70">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '角色攻略', to: '/characters' }, { label: guide.characterName }]} />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">{guide.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-50/75 md:text-sm">
              {character ? (
                <Link
                  to={`/characters/${character.id}`}
                  className="inline-flex cursor-pointer items-center gap-1.5 transition-colors hover:text-accent-300"
                >
                  <i className="ri-user-star-line"></i>
                  关联角色：{guide.characterName}
                </Link>
              ) : null}
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {formatDate(guide.date)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {guide.readTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-4">
          <aside className="hidden lg:col-span-1 lg:block">
            <div className="sticky top-24">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">
                  <a href="#toc">目录导航</a>
                </h2>
                <ul className="mt-3 space-y-1.5">
                  {guide.sections.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#${section.heading}`}
                        className={`block cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
                          activeSection === index
                            ? 'bg-primary-50 font-medium text-primary-700'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                        }`}
                      >
                        {section.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-bold text-foreground-950">
                  <a href="#tags">相关内容标签</a>
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {guide.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex flex-wrap items-center gap-3 border-b border-background-200 pb-6">
              <FavoriteButton id={`guide-${guide.id}`} variant="full" />
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-label text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-line"></i>
                分享
              </button>
              <div className="ml-auto flex flex-wrap gap-2 lg:hidden">
                {guide.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <p className="mt-8 rounded-lg border-l-4 border-primary-400 bg-primary-50 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {guide.summary}
            </p>

            <div className="mt-10 space-y-10">
              {guide.sections.map((section) => (
                <section key={section.heading} id={section.heading} className="scroll-mt-28">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">{section.heading}</h2>
                  <div className="mt-4 space-y-3">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 14)} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <section className="mt-14">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                <a href="#same-category">同类推荐攻略</a>
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {related.map((item, index) => (
                  <Reveal key={item.id} delay={index * 60}>
                    <Link to={`/guides/${item.id}`} className="gs-card-hover group flex gap-3 rounded-xl border border-background-200 bg-background-50 p-3">
                      <div className="h-20 w-24 shrink-0 overflow-hidden rounded-lg">
                        <img src={item.cover} alt={item.title} title={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">{item.title}</h3>
                        <p className="mt-1.5 text-xs text-foreground-500">{item.readTime}</p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={guide.title} url={currentUrl} />
    </div>
  );
}